import React from 'react'

import { UI2FormSectionErrorContext } from './ui2-form-section-error-context'
import { UI2FormSectionFillContext } from './ui2-form-section-fill-context'

interface IUI2FormFieldErrorReporterProps {
  hasError: boolean
  /**
   * Whether this bound field currently counts as unfilled for the wizard's
   * silent-replay auto-advance gate. Undefined when the wrapped component is
   * not a bound field (banners, layout, section itself) — the fill signal is
   * skipped in that case.
   */
  isUnfilled?: boolean
  children: React.ReactNode
}

let reporterCounter = 0

// Wraps each entity-rendered field so the nearest UI2FormSection learns which
// descendants are in error and which are still unfilled. ID is generated
// per-mount so unstable frame keys don't leak entries into either tracking
// set.
export const UI2FormFieldErrorReporter: React.FC<IUI2FormFieldErrorReporterProps> = ({
  hasError,
  isUnfilled,
  children,
}) => {
  const { reportError } = React.useContext(UI2FormSectionErrorContext)
  const { reportUnfilled } = React.useContext(UI2FormSectionFillContext)
  const idRef = React.useRef<string>()
  if (!idRef.current) {
    reporterCounter += 1
    idRef.current = `field:${reporterCounter}`
  }
  const id = idRef.current
  const contributesFill = isUnfilled !== undefined

  React.useEffect(() => {
    reportError(id, hasError)
  }, [id, hasError, reportError])

  React.useEffect(() => {
    return () => reportError(id, false)
  }, [id, reportError])

  React.useEffect(() => {
    if (!contributesFill) return
    reportUnfilled(id, !!isUnfilled)
  }, [id, contributesFill, isUnfilled, reportUnfilled])

  React.useEffect(() => {
    if (!contributesFill) return
    return () => reportUnfilled(id, false)
  }, [id, contributesFill, reportUnfilled])

  return <>{children}</>
}

export function hasFieldErrors(errors: any): boolean {
  return errors != null && (Array.isArray(errors) ? errors.length > 0 : true)
}
